/**
 * React komponent na zachytávanie chýb v aplikácii.
 * Loguje chyby na server, zobrazuje alert.
 * vynechať Unhandled Promise Rejection: UnknownError: Internal error opening backing store for indexedDB.open.
 * vynechať Unhandled Promise Rejection: AbortError: The transaction was aborted, so the request cannot be fulfilled.
 * vynechať Unhandled Promise Rejection: UnknownError: Connection to Indexed Database server lost. Refresh the page to try again
 * @module ErrorBoundary
 */
import React, { Component } from "react";
import { FRONTEND_ERROR_LOG_URL, errorLogUrl } from "./UrlHelper";
import axios from "axios";
import styles from "./Feuatures.module.sass";
import { FileWarning } from "lucide-react";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, isAvaibleForPage: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error, errorInfo) {
    console.log("Zachytená chyba v ErrorBoundary:", error, errorInfo);

    if (this.state.hasError) return; // Prevent infinite loop

    this.setState({ hasError: true, error, isAvaibleForPage: true });

    const stackLines = error.stack ? error.stack.split("\n") : [];
    const mainStackLine =
      stackLines.length > 1 ? stackLines[1].trim() : "No stack trace available";

    await axios
      .post(
        FRONTEND_ERROR_LOG_URL,
        {
          error:
            error.toString() +
            "\n" +
            mainStackLine +
            "\n" +
            errorInfo.componentStack +
            "\n" +
            `URL adresa: ${window.location.href}`,
        },
        {
          headers: {
            Authorization: "Bearer " + window.localStorage.getItem("authToken"),
          },
        },
      )
      .then(function (response) {
        console.log("detail", response);
      })
      .catch(() => {
        console.error("Chyba pri odesílání logu na server");
      });
  }

  componentDidMount() {
    window.onerror = async (message, source, lineno, colno, error) => {
      console.log("Globálna chyba:", message, source, lineno, colno, error);
      if (this.state.hasError) return; // Prevent infinite loop

      // vynimky, ktoré nechceme logovat
      const ignoredErrors = [
        "Internal error opening backing store for indexedDB.open",
        "The transaction was aborted, so the request cannot be fulfilled",
        "Connection to Indexed Database server lost. Refresh the page to try again",
        "Encountered disk full while committing transaction",
      ];
      if (ignoredErrors.some((ignored) => message.includes(ignored))) {
        console.log("Ignorovaná chyba:", message);
        return;
      }

      // Extrahuj názov súboru z URL
      let fileName = "Neznámy súbor";
      if (source) {
        try {
          const url = new URL(source);
          const pathParts = url.pathname.split("/");
          fileName = pathParts[pathParts.length - 1] || source;
        } catch (e) {
          // Ak source nie je validná URL, použij source priamo
          const pathParts = source.split("/");
          fileName = pathParts[pathParts.length - 1] || source;
        }
      }

      // Extrahuj stack trace ak je dostupný
      let stackTrace = "";
      if (error && error.stack) {
        stackTrace = `\nStack trace:\n${error.stack}`;
      }

      this.setState({ hasError: true, error, isAvaibleForPage: false });
      await axios
        .post(FRONTEND_ERROR_LOG_URL, {
          error: `Globálna chyba: ${message} \n Súbor: ${fileName} \n Source URL: ${source} \n Riadok: ${lineno}, Stĺpec: ${colno} \n URL adresa: ${window.location.href} \n Error: ${error}${stackTrace}`,
        })
        .then(function (response) {
          console.log("detail", response);
        })
        .catch((err) => {
          console.error("Chyba pri odosielaní logu na server", err);
        });
    };

    this.handlePromiseRejection = async (event) => {
      console.log("Unhandled; Promise Rejection:", event);

      if (this.state.hasError) return; // Prevent infinite loop

      // Extrahuj informácie o súbore z error.stack
      let errorMessage = "";
      let fileName = "Neznámy súbor";

      if (event.reason) {
        if (event.reason instanceof Error) {
          errorMessage = event.reason.toString();
          // Extrahuj názov súboru zo stack trace
          if (event.reason.stack) {
            const stackLines = event.reason.stack.split("\n");
            // Hľadaj prvý riadok so súborom (zvyčajne druhý riadok)
            for (let i = 1; i < stackLines.length; i++) {
              const line = stackLines[i];
              // Hľadaj pattern ako "at functionName (file:///path/to/file.js:line:col)"
              const match = line.match(/\(([^)]+)\)/);
              if (match) {
                const filePath = match[1];
                const pathParts = filePath.split("/");
                fileName = pathParts[pathParts.length - 1] || filePath;
                break;
              }
            }
            errorMessage += `\nStack trace:\n${event.reason.stack}`;
          }
        } else {
          errorMessage = String(event.reason);
        }
      }

      // vynimky, ktoré nechceme logovat
      const ignoredErrors = [
        "Internal error opening backing store for indexedDB.open",
        "The transaction was aborted, so the request cannot be fulfilled",
        "Connection to Indexed Database server lost. Refresh the page to try again",
        "Encountered disk full while committing transaction",
      ];
      if (ignoredErrors.some((ignored) => errorMessage.includes(ignored))) {
        console.log("Ignorovaná chyba:", errorMessage);
        return;
      }

      this.setState({
        hasError: true,
        error: event.reason,
        isAvaibleForPage: false,
      });
      await axios
        .post(FRONTEND_ERROR_LOG_URL, {
          error: `Unhandled Promise Rejection: ${errorMessage} \n Súbor: ${fileName} \n URL adresa: ${window.location.href}`,
        })
        .then(function (response) {
          console.log("detail", response);
        })
        .catch((err) => {
          console.error("Chyba pri odosielaní logu na server", err);
        });
    };

    window.addEventListener("unhandledrejection", this.handlePromiseRejection);
  }

  componentWillUnmount() {
    window.onerror = null;
    window.removeEventListener(
      "unhandledrejection",
      this.handlePromiseRejection,
    );
  }

  render() {
    if (this.state.hasError && this.state.isAvaibleForPage) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#282c34",
            color: "#ffffff",
            padding: "32px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: "640px", width: "100%" }}>
            <div
              style={{
                fontSize: "4rem",
                marginBottom: "16px",
                lineHeight: 1,
              }}
            >
              <FileWarning />
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: "2.5rem",
                letterSpacing: "-0.03em",
              }}
            >
              Niečo sa pokazilo
            </h1>
            <p
              style={{
                margin: "16px 0 24px",
                fontSize: "1.05rem",
                lineHeight: 1.75,
                color: "#d1d5db",
              }}
            >
              Chybu sme zaznamenali. Skúste načítať stránku znova alebo sa na ňu
              vráťte neskôr.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                border: "none",
                borderRadius: "999px",
                padding: "12px 28px",
                backgroundColor: "#61dafb",
                color: "#1b1f23",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "transform 150ms ease, background-color 150ms ease",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.transform = "scale(1.02)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.transform = "scale(1)")
              }
            >
              Obnoviť stránku
            </button>
            <div
              style={{
                marginTop: "28px",
                padding: "20px",
                borderRadius: "20px",
                backgroundColor: "rgba(255,255,255,0.08)",
                color: "#e5e7eb",
                textAlign: "left",
                wordBreak: "break-word",
                fontSize: "0.95rem",
                lineHeight: 1.5,
              }}
            >
              <strong>Informácie o chybe:</strong>
              <pre style={{ margin: "12px 0 0", whiteSpace: "pre-wrap" }}>
                {this.state.error
                  ? this.state.error.toString()
                  : "Chyba nie je dostupná."}
              </pre>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
